/**
 * SwipePager — a horizontal finger swipe turns a paged deck.
 *
 * "Should be possible to scroll left right on spells in moon dream on mobile
 * through swiping, but keep pagination as is" (owner, 28 October 2026). The
 * pager's Prev/Next and its dots stay exactly as they are; a swipe is a second
 * way to press them, so `onSwipe` calls the same page turn the buttons do.
 *
 * What counts:
 *   - touch or pen only. A mouse never swipes: on a desktop a press-and-drag
 *     over cards is a hover path, not a gesture.
 *   - one finger. A second finger is a pinch (the card magnifier), and the
 *     swipe is abandoned for the rest of that touch.
 *   - horizontal. The axis is decided once, when the finger has travelled the
 *     same 12px slop the tap and the card drag split on (CardDrag owns that
 *     number); a mostly vertical start is left to the page for good.
 *   - far or fast. Past a fifth of the deck's width, or a quick flick past
 *     24px. A short slow drift is a finger that changed its mind.
 *
 * The host gives the deck `touch-action: pan-y` so the browser never claims a
 * horizontal pan (it would send pointercancel), while vertical scrolling and
 * the root's no-zoom policy (touch-policy.css) are untouched.
 *
 * While the finger is down the `follow` element trails it, damped (and more so
 * toward a page that does not exist), and settles back on release: the
 * direct-manipulation answer a native pager gives. A swipe that moved past the
 * slop is never also a tap, so the click a browser may still synthesize after
 * it is swallowed once.
 *
 * Contract: tests/unit/ui/swipePager.test.js.
 */
import { CardDrag } from './card-drag.js';

export type SwipeDirection = 'next' | 'prev';
export interface SwipePagerOptions {
  onSwipe: (direction: SwipeDirection) => void;
  /** Whether a page exists in that direction (defaults to yes). */
  canSwipe?: (direction: SwipeDirection) => boolean;
  /** The element that trails the finger while it swipes. */
  follow?: HTMLElement | null;
}
export interface SwipePagerApi {
  attach(el: HTMLElement, options: SwipePagerOptions): () => void;
}

const SwipePager = {} as SwipePagerApi;

(function () {
  const FALLBACK_SLOP_PX = 12;
  const DISTANCE_SHARE = 0.2;
  const MIN_DISTANCE_PX = 40;
  const FLICK_PX = 24;
  const FLICK_PX_PER_MS = 0.35;
  /* Wider than 45°: a thumb sweeping across a phone arcs. */
  const AXIS_RATIO = 1.15;
  const FOLLOW = 0.4;
  const FOLLOW_EDGE = 0.12;
  const SETTLE_MS = 180;
  const CLICK_SWALLOW_MS = 450;

  function slopPx(): number {
    try {
      const published = (CardDrag as { touchSlopPx?: () => number })?.touchSlopPx?.();
      return Number.isFinite(published) ? Number(published) : FALLBACK_SLOP_PX;
    } catch (_) {
      return FALLBACK_SLOP_PX;
    }
  }

  function reducedMotion(): boolean {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    } catch (_) {
      return false;
    }
  }

  function attach(el: HTMLElement, options: SwipePagerOptions): () => void {
    if (!el || typeof options?.onSwipe !== 'function') return () => {};
    const canSwipe = (direction: SwipeDirection) => (
      typeof options.canSwipe === 'function' ? options.canSwipe(direction) !== false : true
    );
    const follow = options.follow || null;
    let press: null | {
      id: number;
      x: number;
      y: number;
      t: number;
      dx: number;
      axis: 'x' | 'y' | null;
    } = null;
    let swallowUntil = 0;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    const placeFollower = (dx: number) => {
      if (!follow || reducedMotion()) return;
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      follow.style.transition = 'none';
      const toward: SwipeDirection = dx < 0 ? 'next' : 'prev';
      const shift = dx * (canSwipe(toward) ? FOLLOW : FOLLOW_EDGE);
      follow.style.transform = `translate3d(${Math.round(shift * 10) / 10}px, 0px, 0px)`;
    };
    const settleFollower = () => {
      if (!follow || !follow.style.transform) return;
      if (reducedMotion() || !follow.isConnected) {
        follow.style.transform = '';
        follow.style.transition = '';
        return;
      }
      follow.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`;
      follow.style.transform = '';
      settleTimer = setTimeout(() => {
        settleTimer = null;
        follow.style.transition = '';
      }, SETTLE_MS);
    };

    const end = () => {
      press = null;
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      if (!event.isPrimary || press) {
        // A second finger: a pinch, never a swipe.
        if (press) {
          press = null;
          settleFollower();
        }
        return;
      }
      press = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        t: event.timeStamp,
        dx: 0,
        axis: null,
      };
    };

    const onMove = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      if (!press.axis) {
        if (Math.hypot(dx, dy) < slopPx()) return;
        press.axis = Math.abs(dx) > Math.abs(dy) * AXIS_RATIO ? 'x' : 'y';
      }
      if (press.axis !== 'x') return;
      press.dx = dx;
      placeFollower(dx);
    };

    const onUp = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      const done = press;
      end();
      if (done.axis !== 'x') return;
      swallowUntil = Date.now() + CLICK_SWALLOW_MS;
      const dx = event.clientX - done.x;
      const elapsed = Math.max(1, event.timeStamp - done.t);
      const width = el.getBoundingClientRect().width || 0;
      const far = Math.abs(dx) >= Math.max(MIN_DISTANCE_PX, width * DISTANCE_SHARE);
      const flick = Math.abs(dx) >= FLICK_PX && Math.abs(dx) / elapsed >= FLICK_PX_PER_MS;
      settleFollower();
      if (!far && !flick) return;
      const direction: SwipeDirection = dx < 0 ? 'next' : 'prev';
      if (!canSwipe(direction)) return;
      options.onSwipe(direction);
    };

    const onCancel = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      end();
      settleFollower();
    };

    const onClick = (event: MouseEvent) => {
      if (Date.now() > swallowUntil) return;
      swallowUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    };

    el.addEventListener('pointerdown', onDown as EventListener);
    el.addEventListener('pointermove', onMove as EventListener);
    el.addEventListener('pointerup', onUp as EventListener);
    el.addEventListener('pointercancel', onCancel as EventListener);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('pointerdown', onDown as EventListener);
      el.removeEventListener('pointermove', onMove as EventListener);
      el.removeEventListener('pointerup', onUp as EventListener);
      el.removeEventListener('pointercancel', onCancel as EventListener);
      el.removeEventListener('click', onClick, true);
      if (settleTimer) clearTimeout(settleTimer);
      press = null;
    };
  }

  SwipePager.attach = attach;
})();

export { SwipePager };
